import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useJsApiLoader } from "@react-google-maps/api";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { IoIosArrowDown } from "react-icons/io";

import { VoyageDetails } from "Components/Tracking";
import { useCustomerPortalContext } from "../../../../../../context/CustomerPortalContext";
import { useSecretManager } from "../../../../../../hooks/useSecretManager";
import { CustomerPortalShipment } from "../../../../../../models/customerPortalShipment";
import { ContainerPosition, ContainerStatus } from "../../../../../../models/marine.traffic";
import { Shipment } from "../../../../../../models/shipments";
import MarineTrafficService from "../../../../../../services/marine.traffic.service";
import { shipment } from "../../../../../../translate/locales/en_US";
import env from "../../../../../../util/env";
import { CustomerPortalCard } from "../../CustomerPortalCard";
import { StyledMenu } from "../../styles";
import useStyles, {
  ButtonOrders,
  Divider,
  LoadingContent,
  LoadingHeader,
  LoadingLeft,
  LoadingRight,
  LoadingSide,
  OrderPickButtonContainer,
  OrderPickText,
  PickOrderWrapper,
  ShipmentMenuItem,
  TrackingComponentWrapper,
} from "./styles";

interface PropsApiLoader {
  onChange?: (isLoaded: boolean) => void;
  googleMapsApiKey: string;
}

interface ITrackingPanelPanelProps {
  shipments: CustomerPortalShipment[];
  isCustomerPortal?: boolean;
}

const libraries: ("drawing" | "geometry" | "places" | "visualization")[] = ["places"];

const JsApiLoader = (props: PropsApiLoader) => {
  const { onChange, googleMapsApiKey } = props;

  const { isLoaded } = useJsApiLoader({
    libraries,
    googleMapsApiKey,
  });

  useEffect(() => {
    if (onChange) onChange(isLoaded);
  }, [isLoaded]);

  return <></>;
};

const CACHE_TIME = 1 * 60 * 60 * 1000; // 1 hour

const TrackingPanel = ({ shipments, isCustomerPortal = false }: ITrackingPanelPanelProps) => {
  const { secret } = useSecretManager(env.SECRET_ID);

  const [mapIsLoaded, setMapIsLoaded] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const {
    activeShipment,
    setActiveShipment,
    containerPosition,
    setContainerPosition,
    containerStatus,
    setContainerStatus,
    initialShipmentId,
    setInitialShipmentId,
  } = useCustomerPortalContext();

  const classes = useStyles();
  const { t: translate } = useTranslation();

  useEffect(() => {
    if (initialShipmentId) {
      const initialShipment = shipments.find((shipment) => shipment.id === initialShipmentId);

      setActiveShipment(initialShipment);
    } else {
      setActiveShipment(shipments[0]);
    }
  }, [shipments, initialShipmentId]);

  const { isFetching: fetchingContainerStatus } = useQuery(
    [`customer.portal.container.status`, activeShipment?.marineTrafficId],
    () => MarineTrafficService.status(activeShipment?.marineTrafficId ?? ""),
    {
      enabled: true,
      refetchOnWindowFocus: false,
      cacheTime: CACHE_TIME,
      retry: false,
      onSuccess: (response) => {
        if (!response.data) setContainerStatus({} as ContainerStatus);
        if (response && response.data) {
          setContainerStatus(response.data);
          const position = {
            TIMESTAMP: activeShipment?.shipmentTracking?.vesselPosition?.updatedAt,
            LAT: activeShipment?.shipmentTracking?.vesselPosition?.latitude ?? "",
            LON: activeShipment?.shipmentTracking?.vesselPosition?.longitude ?? "",
          } as unknown as ContainerPosition;

          setContainerPosition(position);
        }
      },
    }
  );

  const getShipmentPickLabel = useCallback(
    (shipment?: Pick<CustomerPortalShipment, "billOfLanding" | "containerId">) => {
      if (shipment?.billOfLanding) {
        return {
          title: translate("billOfLading"),
          value: shipment?.billOfLanding,
        };
      }

      return {
        title: translate("shipment"),
        value: shipment?.containerId,
      };
    },
    [isCustomerPortal]
  );
  const shipmentPickLabel = useMemo(
    () => getShipmentPickLabel(activeShipment),
    [activeShipment, isCustomerPortal, getShipmentPickLabel]
  );

  const reorderedShipmentArray = () => {
    const shipmentsArray = shipments.filter(
      (shipments) => shipments.containerId !== activeShipment?.containerId
    );

    return shipmentsArray.map((shipment) => {
      const data = getShipmentPickLabel(shipment);

      return (
        <ShipmentMenuItem key={shipment?.containerId} onClick={() => handleShipmentPick(shipment)}>
          <span>{`${data.title} #${data.value}`}</span>
        </ShipmentMenuItem>
      );
    });
  };

  const handleShipmentPick = (shipment: Shipment) => {
    setActiveShipment(shipment);
    setInitialShipmentId(undefined);
    setAnchorEl(null);
  };

  const renderApiLoader = useMemo(() => {
    if (secret && secret?.GOOGLE_MAPS_API_KEY)
      return (
        <JsApiLoader googleMapsApiKey={secret?.GOOGLE_MAPS_API_KEY} onChange={setMapIsLoaded} />
      );
  }, [secret]);

  const hasPadding = useMemo(
    () =>
      !(
        (!activeShipment?.shipmentTracking || !activeShipment?.marineTrafficId) &&
        isCustomerPortal
      ),
    [activeShipment]
  );

  return (
    <CustomerPortalCard fullHeight>
      {renderApiLoader}
      {activeShipment && fetchingContainerStatus ? (
        <>
          <LoadingHeader />
          <LoadingContent>
            <LoadingSide>
              <LoadingLeft />
              <LoadingLeft />
            </LoadingSide>
            <LoadingRight />
          </LoadingContent>
        </>
      ) : (
        shipment && (
          <>
            <PickOrderWrapper>
              <OrderPickButtonContainer>
                <OrderPickText>
                  {shipmentPickLabel.title} <span>#{shipmentPickLabel.value}</span>
                </OrderPickText>
                {shipments.length >= 2 && (
                  <ButtonOrders
                    open={Boolean(anchorEl)}
                    onClick={(event) => setAnchorEl(event.currentTarget)}
                  >
                    <IoIosArrowDown size={20} color="#475467" />
                  </ButtonOrders>
                )}
              </OrderPickButtonContainer>

              <Divider />

              {shipments.length >= 2 && (
                <StyledMenu
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={() => setAnchorEl(null)}
                  transformOrigin={{ horizontal: "left", vertical: "top" }}
                  anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                  PaperProps={{
                    className: classes.paperMenu,
                  }}
                >
                  <ShipmentMenuItem
                    active
                  >{`${shipmentPickLabel.title} #${shipmentPickLabel.value}`}</ShipmentMenuItem>
                  <Divider />
                  {reorderedShipmentArray()}
                </StyledMenu>
              )}
            </PickOrderWrapper>
            <TrackingComponentWrapper hasPadding={hasPadding}>
              <VoyageDetails
                shipment={activeShipment}
                containerStatus={containerStatus}
                containerPosition={containerPosition}
                viewLivePosition={true}
                externalMapIsLoaded={mapIsLoaded}
                isCustomerPortal={isCustomerPortal}
              />
            </TrackingComponentWrapper>
          </>
        )
      )}
    </CustomerPortalCard>
  );
};

export { TrackingPanel };
